/*
Componente principal para el consentimiento de cookies
Combina el botón sticky y el modal de configuración
Integrado con GoogleAnalyticsManager para control condicional

CORREGIDO para Safari (Node 16.20.2, React 16.12.0):
- useCallback en handlers para referencias estables (Safari event delegation)
- setTimeout corto (250ms) en lugar de montarlo inmediato: da tiempo a
  que React termine de montar los event listeners antes de abrir el modal
  (Safari tiene race conditions con esto si se hace durante la fase de mount)
- cleanup correcto via mountedRef cuando el componente se desmonta o cambian deps
- Boton sticky se oculta mientras el modal esta abierto (evita conflictos z-index)
*/

import React, { useState, useEffect, useCallback, useRef } from 'react'
import CookieButton from './CookieButton'
import CookieModal from './CookieModal'
import './CookieConsent.css'

// GoogleAnalyticsManager se manejará desde la aplicación principal
// La librería de componentes no debe tener dependencias directas del manager
let GoogleAnalyticsManager = null

// Función para establecer el manager desde la aplicación principal
if (typeof window !== 'undefined') {
  window.setGoogleAnalyticsManager = (manager) => {
    GoogleAnalyticsManager = manager
  }
}

export default function CookieConsent({
  onAcceptAll,
  onAcceptSelected,
  autoShow = true,
  storageKey = 'cookieConsent'
}) {
  const [showModal, setShowModal] = useState(false)
  const [showButton, setShowButton] = useState(true)
  const [hasConsent, setHasConsent] = useState(false)
  const mountedRef = useRef(true)

  // Verificar si ya existe consentimiento guardado
  useEffect(() => {
    mountedRef.current = true

    // Pequeño delay para que el arbol React termine de montar todos los
    // event listeners antes de abrir el modal. Compatible con Safari:
    // usamos setTimeout corto (250ms) + chequeo mountedRef para evitar
    // updates tras unmount. Esto evita el flash imediato en Safari.
    let showTimer = null

    const scheduleShow = () => {
      if (!autoShow) return
      showTimer = setTimeout(() => {
        if (mountedRef.current) {
          setShowModal(true)
        }
      }, 250)
    }

    try {
      const savedConsent = localStorage.getItem(storageKey)
      if (savedConsent) {
        setHasConsent(true)
        setShowButton(false)
        setShowModal(false)
      } else {
        scheduleShow()
      }
    } catch (error) {
      // localStorage no disponible (Safari ITP / modo privado)
      scheduleShow()
    }

    return () => {
      mountedRef.current = false
      if (showTimer !== null) {
        clearTimeout(showTimer)
      }
    }
  }, [autoShow, storageKey])

  const handleOpenModal = useCallback(() => {
    setShowModal(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
  }, [])

  const handleAcceptAll = useCallback(async () => {
    const allSettings = {
      necessary: true,
      analytics: true,
      functional: true
    }

    const consentData = {
      timestamp: new Date().toISOString(),
      settings: allSettings,
      version: '2.0'
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(consentData))
    } catch (error) {
      // localStorage no disponible (Safari ITP / modo privado)
    }

    setHasConsent(true)
    setShowButton(false)
    setShowModal(false)

    // Actualizar Google Analytics si está disponible
    if (GoogleAnalyticsManager) {
      try {
        await GoogleAnalyticsManager.updateConsentStatus()
      } catch (error) {
        // Silenciar errores de GA
      }
    }

    if (onAcceptAll) onAcceptAll(allSettings)
  }, [storageKey, onAcceptAll])

  const handleAcceptSelected = useCallback(async (settings) => {
    // Filtrar solo las categorías que están explícitamente aceptadas
    const explicitlyAcceptedSettings = {
      necessary: true,
      ...(settings.analytics === true && { analytics: true }),
      ...(settings.functional === true && { functional: true })
    }

    const consentData = {
      timestamp: new Date().toISOString(),
      settings: explicitlyAcceptedSettings,
      version: '2.0'
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(consentData))
    } catch (error) {
      // localStorage no disponible (Safari ITP / modo privado)
    }

    setHasConsent(true)
    setShowButton(false)
    setShowModal(false)

    // Actualizar estado de Google Analytics
    if (GoogleAnalyticsManager) {
      try {
        await GoogleAnalyticsManager.updateConsentStatus()
      } catch (error) {
        // Silenciar errores de GA
      }
    }

    if (onAcceptSelected) onAcceptSelected(explicitlyAcceptedSettings)
  }, [storageKey, onAcceptSelected])

  // Mientras el modal esta abierto, ocultamos el boton flotante para
  // evitar conflictos visuales/de z-index en Safari y mantener foco
  const shouldShowButton = (showButton || hasConsent) && !showModal

  return (
    <React.Fragment>
      <CookieButton
        onClick={handleOpenModal}
        show={shouldShowButton}
      />
      <CookieModal
        open={showModal}
        onClose={handleCloseModal}
        onAcceptAll={handleAcceptAll}
        onAcceptSelected={handleAcceptSelected}
      />
    </React.Fragment>
  )
}
